import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Compass, ExternalLink, Leaf } from "lucide-react";
import ListenButton from "../components/ListenButton";
import { KERALA_AYURVEDA as A } from "../content/keralaAyurveda";

const Section = ({ s, accent = "border-emerald-700" }) => (
  <section className="mb-10">
    <h2 className={`font-heading font-900 text-xl md:text-2xl border-b-2 ${accent} pb-2 mb-3`}>{s.h}</h2>
    {s.img && (
      <figure className="mb-4">
        <img src={s.img} alt={s.cap || s.h} className="w-full max-h-[420px] object-cover" loading="lazy" />
        {s.cap && <figcaption className="text-xs text-muted-foreground mt-1.5">{s.cap} <span className="opacity-70">Photo: Unsplash</span></figcaption>}
      </figure>
    )}
    <div className="text-base leading-relaxed whitespace-pre-line">{s.t}</div>
  </section>
);

/* Special editorial article — linked from the Kerala travel guide. Audio enabled via ListenButton. */
export default function KeralaAyurveda() {
  useEffect(() => {
    document.title = `${A.title} — TravelerPulse.pro`;
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="kerala-ayurveda-article">
      <div className="bg-gradient-to-br from-emerald-900 to-lime-900 text-white">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 py-12">
          <nav className="text-xs text-white/70 flex items-center gap-1 mb-4 flex-wrap">
            <Link to="/explore/india" className="hover:underline">India</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/explore/india/kerala" className="hover:underline" data-testid="ayurveda-back-guide">Kerala Travel Guide</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/specials" className="hover:underline" data-testid="ayurveda-specials-link">TravelerPulse Specials</Link>
          </nav>
          <div className="text-xs font-mono uppercase tracking-widest text-lime-300 flex items-center gap-1.5 mb-2"><Leaf className="w-4 h-4" /> A TravelerPulse Special</div>
          <h1 className="font-heading font-900 text-4xl sm:text-5xl leading-tight">{A.title}</h1>
          <p className="text-lg text-lime-100 mt-2">{A.subtitle}</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-5 lg:px-8 py-8">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-6">
          <ListenButton targetId="kerala-ayurveda-body" label="Listen to this article" />
          <Link to="/explore/india/kerala" className="text-xs text-primary hover:underline inline-flex items-center gap-1" data-testid="ayurveda-guide-link">
            <Compass className="w-3.5 h-3.5" /> Kerala Travel Guide →
          </Link>
        </div>

        <figure className="mb-8">
          <img src={A.hero} alt={A.heroCap} className="w-full max-h-[440px] object-cover" data-testid="ayurveda-hero-img" />
          <figcaption className="text-xs text-muted-foreground mt-1.5">{A.heroCap} <span className="opacity-70">Photo: Unsplash</span></figcaption>
        </figure>

        <div id="kerala-ayurveda-body">
          <h2 className="font-heading font-900 text-2xl md:text-3xl mb-4">{A.heading}</h2>
          <div className="text-base leading-relaxed whitespace-pre-line mb-10 first-letter:text-5xl first-letter:font-900 first-letter:font-heading first-letter:float-left first-letter:mr-2 first-letter:leading-none first-letter:text-emerald-800">
            {A.intro}
          </div>
          {A.sections.map((s) => <Section key={s.h} s={s} />)}

          <section className="mb-10" data-testid="ayurveda-districts">
            <h2 className="font-heading font-900 text-xl md:text-2xl border-b-2 border-emerald-700 pb-2 mb-3">
              Ayurveda Across Kerala — District by District
            </h2>
            <div className="text-base leading-relaxed whitespace-pre-line mb-4">{A.districtsIntro}</div>
            <figure className="mb-5">
              <img src={A.districtsImg} alt={A.districtsCap} className="w-full max-h-[420px] object-cover" loading="lazy" />
              <figcaption className="text-xs text-muted-foreground mt-1.5">{A.districtsCap} <span className="opacity-70">Photo: Unsplash</span></figcaption>
            </figure>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto border border-zinc-300">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-emerald-800 text-white text-left">
                    <th className="px-3 py-2.5 font-700 whitespace-nowrap align-top">District</th>
                    <th className="px-3 py-2.5 font-700 align-top">Important Ayurveda institutions / areas</th>
                    <th className="px-3 py-2.5 font-700 align-top">Notable treatment strengths and services</th>
                  </tr>
                </thead>
                <tbody>
                  {A.districts.map((r, i) => (
                    <tr key={r.d} className={i % 2 ? "bg-emerald-50/40" : "bg-white"}>
                      <td className="px-3 py-2.5 font-700 text-emerald-900 whitespace-nowrap align-top">{r.d}</td>
                      <td className="px-3 py-2.5 align-top text-zinc-700">{r.inst}</td>
                      <td className="px-3 py-2.5 align-top text-zinc-700">{r.str}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Mobile cards */}
            <div className="md:hidden space-y-3">
              {A.districts.map((r) => (
                <div key={r.d} className="border border-zinc-300 bg-white p-3">
                  <div className="font-heading font-900 text-emerald-900">{r.d}</div>
                  <div className="text-xs text-zinc-600 mt-1"><b>Institutions:</b> {r.inst}</div>
                  <div className="text-xs text-zinc-600 mt-1.5"><b>Strengths:</b> {r.str}</div>
                </div>
              ))}
            </div>
          </section>

          {A.sections2.map((s) => <Section key={s.h} s={s} />)}

          <p className="font-heading font-900 text-lg text-emerald-900 mb-6">{A.tagline}</p>
        </div>

        <div className="border-l-4 border-amber-500 bg-amber-50 p-4 mb-8 text-sm text-zinc-700" data-testid="ayurveda-health-note">
          {A.healthNote}
        </div>

        <section className="border border-zinc-300 bg-emerald-50/50 p-5 mb-8" data-testid="ayurveda-links">
          <h2 className="font-heading font-900 text-lg mb-3">References &amp; Further Reading</h2>
          <ul className="space-y-2">
            {A.links.map((l) => (
              <li key={l.label}>
                <a href={l.url} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline inline-flex items-center gap-1.5">
                  <ExternalLink className="w-3.5 h-3.5 shrink-0" /> {l.label}
                </a>
              </li>
            ))}
          </ul>
        </section>

        <Link to="/explore/india/kerala" data-testid="ayurveda-footer-guide"
          className="flex items-center justify-between gap-3 border border-zinc-300 bg-white px-4 py-3 hover:border-emerald-700 transition-colors mb-8">
          <span className="text-sm font-700">Plan your visit — Kerala Travel Guide</span>
          <ChevronRight className="w-4 h-4 shrink-0" />
        </Link>

        <p className="text-[11px] text-muted-foreground">
          Presented by <a href="https://travelerpulse.pro" target="_blank" rel="noreferrer" className="font-700 text-primary hover:underline">TravelerPulse.pro™</a> — photos via Unsplash (free license); verify time-sensitive facts (classifications, accreditations, services) on official sites.
        </p>
      </div>
    </div>
  );
}
